import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableRow, TableCell, TableHead, TableBody, Avatar, Typography, Divider,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import makeStyles from '@mui/styles/makeStyles';
import { useEffectAsync } from '../reactHelper';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionFab from './components/CollectionFab';
import CollectionActions from './components/CollectionActions';
import TableShimmer from '../common/components/TableShimmer';
import SearchHeader, { filterByKeyword } from './components/SearchHeader';
import { usePreference } from '../common/util/preferences';
import { formatTime } from '../common/util/formatter';
import { useDeviceReadonly } from '../common/util/permissions';

const useStyles = makeStyles((theme) => ({
  columnAction: {
    width: '1%',
    paddingRight: theme.spacing(1),
  },
  header1: {
    display: 'flex',
    margin: 20,
  },
  text: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
  },
  header2: {
    position: 'sticky',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginRight: 15,
    padding: 10,
  },
}));

const DevicesPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const t = useTranslation();

  const hours12 = usePreference('twelveHourFormat');

  const deviceReadonly = useDeviceReadonly();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);

  const devices = useSelector((state) => state.devices.items);

  const deviceStatusCount = (status) => Object.values(devices).filter((d) => d.status === status).length;

  const deviceCount = (devices) => items.filter(filterByKeyword(searchKeyword)).map((item) => item.name === devices).length;

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/devices');
      if (response.ok) {
        setItems(await response.json());
      } else {
        throw Error(await response.text());
      }
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  const actionConnections = {
    key: 'connections',
    title: t('sharedConnections'),
    icon: <LinkIcon fontSize="small" />,
    handler: (deviceId) => navigate(`/settings/device/${deviceId}/connections`),
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedDrivers']}>
      <Divider />
      <div className={classes.header1}>
        <Typography sx={{ color: '#2b3643' }} className={classes.text} variant="h6">LISTA DE VÉICULOS</Typography>
        <CollectionFab editPath="/settings/device" />
      </div>
      <div className={classes.header2}>
        <div className={classes.avatar}>
          <Avatar sx={{ width: 50, height: 30, borderRadius: 5, marginLeft: 0.5, color: 'black', backgroundColor: 'green' }}>{`${deviceStatusCount('online')}`}</Avatar>
          <Avatar sx={{ width: 50, height: 30, borderRadius: 5, marginLeft: 0.5, color: 'black', backgroundColor: 'red' }}>{`${deviceStatusCount('offline')}`}</Avatar>
          <Avatar sx={{ width: 50, height: 30, borderRadius: 5, marginLeft: 0.5, marginRight: 1, color: 'black', backgroundColor: 'yellow' }}>{`${deviceStatusCount('unknown')}`}</Avatar>
          <Avatar sx={{ width: 50, height: 30, borderRadius: 5, marginLeft: 0.5, marginRight: 1, color: 'black', backgroundColor: 'blue' }}>{`${deviceCount('devices')}`}</Avatar>
          <SearchHeader keyword={searchKeyword} setKeyword={setSearchKeyword} />
        </div>
      </div>
      <Divider />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('sharedName')}</TableCell>
            <TableCell>{t('deviceIdentifier')}</TableCell>
            <TableCell>{t('sharedPhone')}</TableCell>
            <TableCell>{t('deviceModel')}</TableCell>
            <TableCell>{t('deviceContact')}</TableCell>
            <TableCell>{t('userExpirationTime')}</TableCell>
            <TableCell>{t('userAction')}</TableCell>
            <TableCell className={classes.columnAction} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? items.filter(filterByKeyword(searchKeyword)).map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.name}</TableCell>
              <TableCell>{item.uniqueId}</TableCell>
              <TableCell>{item.phone}</TableCell>
              <TableCell>{item.model}</TableCell>
              <TableCell>{item.contact}</TableCell>
              <TableCell>{formatTime(item.expirationTime, 'date', hours12)}</TableCell>
              <TableCell className={classes.columnAction} padding="none">
                <CollectionActions
                  itemId={item.id}
                  editPath="/settings/device"
                  endpoint="devices"
                  setTimestamp={setTimestamp}
                  customActions={[actionConnections]}
                  readonly={deviceReadonly}
                />
              </TableCell>
            </TableRow>
          )) : (<TableShimmer columns={6} endAction />)}
        </TableBody>
      </Table>
    </PageLayout>
  );
};

export default DevicesPage;
